import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

import "./EmbeddedSoftwareSystems.css";
import "./PageTheme.css";
import serviceImage from "../../../images/engineering-capability-hero.png";

type Block = { title?: string; text?: string; lines?: string[]; raw: string };

const config = {
  "title": "Embedded Software Systems",
  "category": "Software, Platforms & Infrastructure",
  "visual": "software",
  "folder": "EmbeddedSoftwareSystems",
  "sections": [
    [
      "Embedded Software Systems",
      "Engineering Software That Brings Hardware to Life\nEmbedded software is at the heart of every intelligent electronic system. It enables hardware to operate reliably, respond in real time, communicate seamlessly, and deliver the functionality that transforms semiconductor innovation into practical, high-performance products.\n\nDigicomm Semiconductor provides specialized Embedded Software Systems engineering that supports the complete embedded software development lifecycle. From firmware development and board support packages to device drivers, operating system integration, middleware, and application software, our engineering teams work closely with customers to build reliable, scalable, and application-ready embedded systems through disciplined engineering execution and collaborative development.\n\nPrimary CTA\nDiscuss Your Embedded Software Requirements"
    ],
    [
      "Capability Overview",
      "Embedded software forms the intelligence behind modern electronic products, enabling hardware to perform specific functions with reliability, efficiency, and precision. It provides the essential software layer that controls devices, manages hardware resources, enables communication, and supports real-time operation across a wide range of semiconductor and embedded applications.\n\nDigicomm Semiconductor delivers specialized Embedded Software Systems engineering that spans the complete software development lifecycle. From firmware architecture and board support packages to device drivers, operating system integration, middleware development, and application software, our engineering teams collaborate closely with customers to develop robust, scalable, and high-performance embedded solutions that accelerate product development and enable dependable system operation."
    ],
    [
      "Why It Matters\nTransform hardware into intelligent systems through reliable embedded software that delivers precise control and application-specific functionality.\n\nStrengthens Hardware-Software Integration\nEnable seamless interaction between software, processors, peripherals, and hardware platforms through structured engineering.\n\nImproves System Reliability\nDevelop robust software architectures that support stable operation, fault tolerance, and dependable system performance.\n\nAccelerates Product Development\nSupport parallel hardware and software development to reduce engineering cycles and improve project execution.\n\nEnhances Product Maintainability\nBuild scalable software architectures that simplify updates, feature enhancements, and long-term product evolution.\n\nSupports Reliable Product Deployment\nDeliver production-ready embedded software that strengthens product quality, operational confidence, and real-world performance."
    ],
    [
      "Embedded Software Engineering Scope",
      "Embedded Firmware Development\nDevelop reliable firmware that enables precise hardware control, real-time operation, and efficient device functionality across embedded platforms.\n\nBoard Support Package (BSP) Development\nDevelop and customize Board Support Packages that establish seamless interaction between hardware platforms, operating systems, and application software.\n\nDevice Driver Development\nDesign and implement device drivers that enable reliable communication between hardware peripherals and embedded software environments.\n\nOperating System Integration\nIntegrate and optimize Embedded Linux, RTOS, and other operating system environments to support stable, efficient, and application-specific system operation.\n\nMiddleware Development\nDevelop middleware components that enable communication, resource management, protocol handling, and software interoperability across embedded systems.\n\nApplication Software Development\nBuild scalable embedded applications that deliver intelligent functionality, user-specific features, and dependable system performance.\n\nSoftware Verification & Debugging\nPerform software debugging, validation, and functional verification to improve software quality, system stability, and deployment readiness.\n\nSystem Integration & Performance Optimization\nCollaborate across hardware and software engineering teams to integrate complete embedded solutions while optimizing performance, resource utilization, and operational reliability."
    ],
    [
      "Key Benefits",
      "Embedded software is fundamental to the performance, intelligence, and reliability of modern electronic products. Digicomm's Embedded Software Systems engineering helps organizations develop robust software architectures that strengthen hardware-software integration, improve system performance, and accelerate product realization through disciplined engineering practices.\n\nIntelligent Product Functionality\nDevelop embedded software that enables reliable device control, real-time operation, and application-specific functionality across diverse embedded platforms.\n\nSeamless Hardware-Software Integration\nEnable efficient interaction between hardware, firmware, operating systems, and applications through coordinated engineering and structured software development.\n\nImproved System Reliability\nBuild stable and dependable embedded software architectures that enhance product performance, operational consistency, and long-term reliability.\n\nAccelerated Product Development\nSupport parallel hardware and software development, reducing engineering cycles and improving overall project execution efficiency.\n\nScalable Software Architecture\nDevelop modular and maintainable software frameworks that simplify future enhancements, feature expansion, and product evolution.\n\nReliable Product Deployment\nDeliver production-ready embedded software that strengthens product quality, deployment confidence, and long-term operational success."
    ],
    [
      "Technology Expertise",
      "Technology Expertise\nModern embedded systems demand expertise across software architecture, operating systems, processor platforms, communication protocols, and development environments. Digicomm's Embedded Software engineering teams combine strong software engineering practices with hardware awareness to develop reliable, scalable, and high-performance embedded solutions that support intelligent electronic products across diverse application domains.\n\nSoftware Development\nEmbedded Firmware Development\nBoard Support Package (BSP) Development\nDevice Driver Development\nMiddleware Development\nEmbedded Application Development\nOperating Systems\nEmbedded Linux\nReal-Time Operating Systems (RTOS)\nBootloader Development\nOperating System Porting\nSystem-Level Software Integration\nProcessor Platforms\n(Include only platforms actively supported by Digicomm)\nARM Cortex Series\nRISC-V\nMicrocontrollers (MCUs)\nMicroprocessors (MPUs)\nSystem-on-Chip (SoC) Platforms\nCommunication Protocols\nUART\nSPI\nI²C\nCAN\nUSB\nEthernet\nPCIe\nTCP/IP\nDevelopment Environment\n(Include only tools actively used by Digicomm)\nEmbedded C / C++\nGCC Toolchain\nGDB Debugger\nGit Version Control\nEclipse / VS Code\nContinuous Integration & Build Environments"
    ],
    [
      "Why Digicomm",
      "Why Digicomm\nEmbedded software is fundamental to how modern electronic products perform, communicate, and evolve. Delivering reliable embedded systems requires more than software development—it demands a deep understanding of hardware interaction, real-time operation, system integration, and disciplined engineering execution. Digicomm combines specialized embedded software expertise with collaborative engineering practices to help customers build intelligent, reliable, and scalable products with confidence.\n\nWhat Sets Us Apart\nSpecialized Embedded Software Expertise\nDedicated engineering teams experienced across firmware development, board support packages, device drivers, operating system integration, middleware, and embedded application development.\n\nHardware-Software Integration\nA collaborative engineering approach that ensures seamless interaction between embedded software, hardware platforms, peripherals, and system architectures.\n\nDisciplined Software Engineering\nStructured development methodologies focused on software quality, maintainability, performance, and dependable product outcomes.\n\nScalable Engineering Engagement\nFlexible engagement models that adapt to changing product requirements, technology platforms, and development schedules while maintaining execution continuity.\n\nCommitment to Reliable Product Delivery\nA continuous focus on developing production-ready embedded software that strengthens product functionality, operational reliability, and long-term system performance."
    ],
    [
      "Related Engineering Capabilities",
      "Related Specialized Engineering Capabilities\n\nHardware Platform Engineering\nDevelop robust hardware platforms that complement embedded software through system integration, prototyping, validation, and engineering development.\nLearn More →\n\nNetworking Solutions\nEnable reliable communication, connectivity, and data exchange across embedded, industrial, and connected electronic systems.\nLearn More →\n\nRelated Core Engineering Services\nFPGA Engineering\nAccelerate hardware-software co-development through FPGA-based prototyping, hardware validation, and system integration.\nLearn More →\n\nSilicon Realization\nSupport coordinated engineering execution that brings together hardware, embedded software, and implementation disciplines to enable reliable product realization.\nLearn More →\n\nRelated Engineering Engagement Models\nEngineering Consulting\nLeverage specialized embedded software expertise to solve complex development challenges and strengthen product engineering.\nLearn More →\n\nDedicated Engineering Teams\nExtend your engineering organization with experienced embedded software engineers who integrate seamlessly with your product development teams.\nLearn More →"
    ],
    [
      "Let's Build Intelligent Embedded Systems Together",
      "Let's Build Intelligent Embedded Systems Together:\nReliable embedded software is fundamental to creating intelligent, high-performance electronic products. Whether you're developing next-generation embedded devices, integrating hardware and software, optimizing real-time system performance, or expanding your engineering capabilities, Digicomm Semiconductor is ready to support your team with specialized embedded software expertise, disciplined engineering execution, and collaborative product development.\n\nPrimary CTA\nDiscuss Your Embedded Software Requirements"
    ]
  ]
} as const;

const splitBlocks = (raw: string): string[] =>
  raw.split(/\n\s*\n/g).map((x) => x.trim()).filter(Boolean);

const asBlock = (raw: string): Block => {
  const lines = raw.split("\n").map((x) => x.trim()).filter(Boolean);
  if (lines.length >= 2 && lines[0].length <= 74) {
    const rest = lines.slice(1);
    const listLike = rest.length >= 3 && rest.every((x) => x.length < 78);
    if (listLike) return { title: lines[0], lines: rest, raw };
    return { title: lines[0], text: rest.join(" "), raw };
  }
  if (lines.length === 1) return { text: lines[0], raw };
  return { text: lines.join(" "), raw };
};

const getHero = (section: readonly string[], pageTitle: string) => {
  const merged = section.join("\n\n");
  const blocks = splitBlocks(merged);
  const filtered = blocks.filter((x) => x !== "Primary CTA");
  if (filtered[0] === pageTitle) filtered.shift();

  const first = filtered.shift() || pageTitle;
  const firstLines = first.split("\n").map((x) => x.trim()).filter(Boolean);
  const headline = firstLines[0] || pageTitle;
  const body: string[] = [];

  if (firstLines.length > 1) body.push(firstLines.slice(1).join(" "));
  filtered.forEach((b) => body.push(b));

  let cta = "";
  if (body.length && body[body.length - 1].length < 85) cta = body.pop() || "";
  return { headline, body, cta };
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EngineeringVisual: React.FC = () => (
  <div className="ses-visual service-engineering-image">
    <img src={serviceImage} alt="Semiconductor architecture for embedded software systems" />
  </div>
);

const ContentPanel: React.FC<{ raw: string; index: number }> = ({ raw, index }) => {
  const blocks = splitBlocks(raw).filter((x) => x !== "Primary CTA");
  return (
    <div className="ses-block-grid">
      {blocks.map((rawBlock, i) => {
        const block = asBlock(rawBlock);
        const long = rawBlock.length > 430;
        const labelOnly =
          !block.title &&
          !!block.text &&
          block.text.length < 80 &&
          (/[:：]$/.test(block.text) || i === 0);

        return (
          <article
            className={`ses-block ${long ? "ses-block--wide" : ""} ${labelOnly ? "ses-block--label" : ""}`}
            key={`${index}-${i}`}
          >
            {block.title && <h3>{block.title}</h3>}
            {block.text && (labelOnly ? <h3>{block.text}</h3> : <p>{block.text}</p>)}
            {block.lines && <ul>{block.lines.map((line) => <li key={line}>{line}</li>)}</ul>}
          </article>
        );
      })}
    </div>
  );
};

const RelatedPanel: React.FC<{ raw: string }> = ({ raw }) => {
  const blocks = splitBlocks(raw);
  const routeMap: Record<string, string> = {
    "Analog & Mixed Signal Design & Layout": "/services/analog-mixed-signal",
    "Analog & Mixed Signal": "/services/analog-mixed-signal",
    "Static Timing Analysis (STA)": "/services/static-timing-analysis",
    "FPGA Engineering": "/services/fpga-engineering",
    "Embedded Software Systems": "/services/embedded-software-systems",
    "Hardware Platform Engineering": "/services/hardware-platform-engineering",
    "Networking Solutions": "/services/networking-solutions",
    "Silicon Validation": "/services/silicon-validation",
    "EDA & Design Automation": "/services/eda-design-automation",
    "Product Engineering": "/services/product-engineering",
    "ASIC & SoC Design": "/services/asic-soc-design",
    "Physical Design": "/services/physical-design",
    "Verification Services": "/services/verification",
  };

  const cards: React.ReactNode[] = [];
  let group = "";

  blocks.forEach((b, i) => {
    if (/^Related .*:$/.test(b.trim())) {
      group = b.trim();
      return;
    }
    if (b === "Learn More →") return;

    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
    const title = lines[0];
    const desc = lines.slice(1).join(" ");

    if (routeMap[title]) {
      cards.push(
        <Link className="ses-related-card" to={routeMap[title]} key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
          <em>Learn More <Arrow/></em>
        </Link>
      );
    } else {
      cards.push(
        <article className="ses-related-card" key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
        </article>
      );
    }
  });

  return <div className="ses-related-grid">{cards}</div>;
};

const Page: React.FC = () => {
  const sections = config.sections as readonly (readonly string[])[];
  const hero = getHero(sections[0] || [], config.title);

  const lastSection = sections[sections.length - 1] || [];
  const lastRaw = lastSection.join("\n\n");
  const lastBlocks = splitBlocks(lastRaw).filter((x) => x !== "Primary CTA");
  const ctaLabel =
    lastBlocks.length && lastBlocks[lastBlocks.length - 1].length < 90
      ? lastBlocks[lastBlocks.length - 1]
      : "";
  const ctaContent = ctaLabel ? lastBlocks.slice(0, -1) : lastBlocks;
  const hasRelated = sections.length === 9;

  useEffect(() => {
    document.title = `${config.title} | Digicomm Semiconductor`;
  }, []);

  return (
    <Fragment>
      <div className={`specialized-service-page inner-page embedded-redesign`}>
        <div className="body_wrap o-clip">
          <Header />

          <main>
            <PageTitle pageTitle={config.title} pagesub={config.title} />

            <section className="ses-hero">
              <div className="ses-shell ses-two-col">
                <div className="ses-hero__copy">
                  <h1>{hero.headline}</h1>
                  {hero.body.map((p, i) => <p key={i}>{p}</p>)}
                  {hero.cta && (
                    <Link className="ses-btn" to="/contact">
                      {hero.cta}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>

            {sections.slice(1, hasRelated ? -2 : -1).map((section, idx) => {
              const raw = section.join("\n\n");
              const blocks = splitBlocks(raw);
              const lead = blocks[0] || "";
              const firstLine = lead.split("\n")[0] || "";

              return (
                <section className={`ses-section ses-section--${idx + 2} ${idx % 2 ? "ses-section--alt" : ""}`} key={idx}>
                  <div className="ses-shell">
                    <div className="ses-section-head">
                      <span>{String(idx + 2).padStart(2, "0")}</span>
                      <h2>{firstLine}</h2>
                    </div>
                    <ContentPanel raw={raw} index={idx}/>
                  </div>
                </section>
              );
            })}

            {hasRelated && (
              <section className="ses-section ses-related">
                <div className="ses-shell">
                  <RelatedPanel raw={sections[sections.length - 2].join("\n\n")}/>
                </div>
              </section>
            )}

            <section className="ses-cta">
              <div className="ses-shell ses-two-col">
                <div className="ses-cta__copy">
                  {ctaContent.map((b, i) => {
                    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
                    return i === 0
                      ? <h2 key={i}>{lines.join(" ")}</h2>
                      : <p key={i}>{lines.join(" ")}</p>;
                  })}

                  {ctaLabel && (
                    <Link className="ses-btn" to="/contact">
                      {ctaLabel}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>
          </main>

          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default Page;

